import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';

import { MonoTypeOperatorFunction, Observable, throwError, timer } from 'rxjs';
import { map, catchError, retry } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { DatePipe } from '@angular/common';
import {
  IAmanoOneFacilityResponse,
  IParcsGetLotConfigurationResponse,
  IParcsNewLotConfigurationRequest,
  IParcsListResponse,
  IAmanoOneAccessGroups,
  IAmanoOneAccessHolderUpdateResponse,
  IAmanoOne_AccessGroupsWithMembershipResponse,
  IAmanoOne_ParkerSyncComparisonForLotResponse,
  IFlash_ParkerSyncComparisonForLotApiResponse,
  IFlash_ParkerSyncComparisonForLotResponse,
  IAmanoOneScheduleLprCredentialStatusUpdateRequest,
  IAmanoOneCredentialTypeRequest,
  IAmanoOneCredentialTypeResponse,
  IAmanoOneCredentialSystemCodeRequest, 
  IAmanoOneCredentialSystemCodeResponse,
  IFlashValidateCredentialResponse,
  IFlashValidateCredentialRequest,
  IFlashProfilesByLotResponse,
  IFlashMonthlyAccount,
  FlashAccountByLotResponse,
  IFlashResetToNeutralResponse,
  IFlashResetEntireLotKickoffResponse,
  IFlashResetEntireLotResult,
  IFlashUpdateAccountProfileRequest,
  IFlashUpdateAccountProfileResponse,
  IFlashAddAccountForExistingParkerRequest,
  IFlashAddAccountForExistingParkerResponse,
  IFlashPool,
  IFlashLinkAccountResponse,
  IFlashCertificationData,
  IFlashDailyAccountDataKickoffResponse,
  IFlashDailyAccountJobStatus
} from 'src/app/shared/code/interfaces/parcs-interfaces';
import { IParkerProfileDetail } from 'src/app/shared/interfaces';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

const httpSpaceOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
  }),
};

const httpFileDownloadHttOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    observe: 'response',
    responseType: 'blob',
    accept: '*',
  }),
};

@Injectable({
  providedIn: 'root', // <-- ADD THIS OBJECT
})
export class ParcsDataService {
  baseUrl: string = '';
  spaceArEngineBaseUrl: string = '';

  httpOptions: any;

  //Services
  _datePipe = inject(DatePipe);

  constructor(private http: HttpClient) {
    this.baseUrl = environment.RestServer;
    this.spaceArEngineBaseUrl = environment.spaceEngineRestServer;
  }

  // Retries transient HTTP failures (network error, 408, 429, or 5xx) up to 3 times
  // using exponential backoff (1s, 2s, 4s). Non-transient errors (e.g. 4xx) fail immediately.
  // Only apply this to idempotent GET requests so writes are never re-submitted.
  private retryTransient<T>(): MonoTypeOperatorFunction<T> {
    return retry<T>({
      count: 3,
      delay: (error: HttpErrorResponse, retryCount: number) => {
        const isTransient = error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
        if (!isTransient) {
          return throwError(() => error);
        }
        const backoffMs = Math.min(1000 * 2 ** (retryCount - 1), 8000);
        return timer(backoffMs);
      },
    });
  }

  // **************** FLASH SPECIFIC PARCS API CALLS **************** //

  // Returns HTTP 200 even on a Flash-side business failure; branch on isSuccess in the body, not on transport status.
  flash_ResetAccountToNeutral(lotNumber: string, parkerId: string): Observable<IFlashResetToNeutralResponse> {
    return this.http
      .post<IFlashResetToNeutralResponse>(
        `${this.baseUrl}parcs/flash/account/resettoneutral/${encodeURIComponent(lotNumber)}/${encodeURIComponent(parkerId)}`,
        null,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Kicks off resetting EVERY active Parker's Flash account to neutral for the whole lot. Can take
  // several minutes to an hour for a large lot, so this returns immediately (202) with a JobId -
  // poll flash_GetResetEntireLotJobStatus for live progress and the final tally.
  flash_ResetEntireLot(lotNumber: string): Observable<IFlashResetEntireLotKickoffResponse> {
    return this.http
      .post<IFlashResetEntireLotKickoffResponse>(
        `${this.baseUrl}parcs/flash/resetentirelot/${encodeURIComponent(lotNumber)}`,
        null,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Polls the status/progress of a Flash_ResetEntireLot job by the JobId returned from
  // flash_ResetEntireLot. isComplete stays false while Status is 'Queued' or 'Processing'.
  flash_GetResetEntireLotJobStatus(jobId: string): Observable<IFlashResetEntireLotResult> {
    return this.http
      .get<IFlashResetEntireLotResult>(
        `${this.baseUrl}parcs/flash/resetentirelot/status/${encodeURIComponent(jobId)}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Returns HTTP 200 even when the account does not exist; branch on isSuccess in the body, not on transport status.
  flash_GetAccountByLotParkerId(lotNumber: string, parkerId: string): Observable<FlashAccountByLotResponse> {
    return this.http
      .get<
        FlashAccountByLotResponse
      >(`${this.baseUrl}parcs/flash/account/${encodeURIComponent(lotNumber)}/${encodeURIComponent(parkerId)}?accountNumberType=External`, httpOptions)
      .pipe(this.retryTransient(), catchError(this.handleError));
  }

  // Returns HTTP 200 even on a Flash-side business failure; branch on isSuccess/code in the body, not on transport status.
  flash_GetProfilesByLotNumber(lotNumber: string): Observable<IFlashProfilesByLotResponse> {
    return this.http
      .get<
        IFlashProfilesByLotResponse
      >(`${this.baseUrl}parcs/flash/profiles/${encodeURIComponent(lotNumber)}`, httpOptions)
      .pipe(this.retryTransient(), catchError(this.handleError));
  }

  // Changes the Profile, Pool and Allow Passback settings on an existing Flash account; every other
  // value is carried forward server-side. Returns HTTP 200 even on a Flash-side failure, so branch on isSuccess.
  flash_UpdateAccountProfile(
    lotNumber: string,
    parkerId: string,
    request: IFlashUpdateAccountProfileRequest,
  ): Observable<IFlashUpdateAccountProfileResponse> {
    return this.http
      .post<IFlashUpdateAccountProfileResponse>(
        `${this.baseUrl}parcs/flash/account/profile/${encodeURIComponent(lotNumber)}/${encodeURIComponent(parkerId)}`,
        request,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Creates a Flash account for a Parker that already exists in spACE-AR but is not linked to Flash.
  // Returns HTTP 200 even on a Flash-side failure, so branch on isSuccess.
  flash_AddAccountForExistingParker(
    lotNumber: string,
    parkerId: string,
    request: IFlashAddAccountForExistingParkerRequest,
  ): Observable<IFlashAddAccountForExistingParkerResponse> {
    return this.http
      .post<IFlashAddAccountForExistingParkerResponse>(
        `${this.baseUrl}parcs/flash/account/add/${encodeURIComponent(lotNumber)}/${encodeURIComponent(parkerId)}`,
        request,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Attaches the ParkerId as the external account number on a Flash account created in the Flash
  // Portal. The account number and GUID are resolved server-side from a single read, because Flash
  // validates the pair. Returns HTTP 200 even on a Flash-side failure, so branch on isSuccess.
  // accountNumberType must be 'Flash' or 'External' - Flash rejects anything else outright.
  flash_LinkPortalAccountToParker(
    lotNumber: string,
    flashAccountNumber: string,
    parkerId: string,
    accountNumberType: string = 'Flash',
  ): Observable<IFlashLinkAccountResponse> {
    return this.http
      .post<IFlashLinkAccountResponse>(
        `${this.baseUrl}parcs/flash/account/link/${encodeURIComponent(lotNumber)}/${encodeURIComponent(flashAccountNumber)}/${encodeURIComponent(parkerId)}?accountNumberType=${encodeURIComponent(accountNumberType)}`,
        null,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // ============== FLASH POOL NAME ADMIN ============== //
  // Flash has no "list pools" endpoint, so pool names are maintained in spACE-AR per lot.

  // TEMPORARY - Flash certification evidence for the GetMonthlyAccount / GetMonthlyContact /
  // GetMonthlyVehicle test cases. Remove with the PARCS tab block once screenshots are taken.
  flash_GetCertificationData(lotNumber: string, parkerId: string): Observable<IFlashCertificationData> {
    return this.http
      .get<IFlashCertificationData>(
        `${this.baseUrl}parcs/flash/certification/${encodeURIComponent(lotNumber)}/${encodeURIComponent(parkerId)}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  flash_GetPoolsByLotNumber(lotNumber: string): Observable<IFlashPool[]> {
    return this.http
      .get<IFlashPool[]>(`${this.baseUrl}parcs/flash/pools/${encodeURIComponent(lotNumber)}`, httpOptions)
      .pipe(this.retryTransient(), catchError(this.handleError));
  }

  flash_AddPool(lotNumber: string, poolName: string): Observable<IFlashPool> {
    return this.http
      .post<IFlashPool>(`${this.baseUrl}parcs/flash/pools/${encodeURIComponent(lotNumber)}`, { poolName }, httpOptions)
      .pipe(catchError(this.handleError));
  }

  flash_DeletePool(poolId: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${this.baseUrl}parcs/flash/pools/${encodeURIComponent(poolId)}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // Takes the complete ordered list, not just the moved pool - moving one row shifts every row
  // between its old and new position.
  flash_UpdatePoolSortOrder(lotNumber: string, orderedPoolIds: string[]): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.baseUrl}parcs/flash/pools/sortorder/${encodeURIComponent(lotNumber)}`,
        orderedPoolIds,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  flash_ValidateCredentials(
    activationData: IFlashValidateCredentialRequest
  ): Observable<IFlashValidateCredentialResponse> {    return this.http
      .post<
        IFlashValidateCredentialResponse
      >(`${this.baseUrl}parcs/flash/activate-location`, activationData, httpOptions)
      .pipe(this.retryTransient());
  }

  // Kicks off the on-demand Flash daily account data pull for a single lot. The endpoint responds
  // HTTP 202 immediately and does the actual Flash sync in a fire-and-forget background task, but
  // pre-inserts the job-header row synchronously and returns its FlashDailyDataFetchId, so the
  // caller can poll flash_GetDailyAccountDataJobStatus for real completion.
  flash_GetDailyAccountDataForLot(lotNumber: string): Observable<IFlashDailyAccountDataKickoffResponse> {
    return this.http
      .get<IFlashDailyAccountDataKickoffResponse>(
        `${this.baseUrl}task/parcs/flash/dailyaccountdata/${encodeURIComponent(lotNumber)}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Polls the status of a single Flash daily account sync run by the FlashDailyDataFetchId returned
  // from flash_GetDailyAccountDataForLot. isComplete stays false while the background task is
  // still running (DateTimeCompletedUtc not yet stamped).
  flash_GetDailyAccountDataJobStatus(flashDailyDataFetchId: string): Observable<IFlashDailyAccountJobStatus> {
    return this.http
      .get<IFlashDailyAccountJobStatus>(
        `${this.baseUrl}task/parcs/flash/dailyaccountdata/status/${encodeURIComponent(flashDailyDataFetchId)}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // ============== END FLASH SPECIFIC PARCS API CALLS ============== //

  // ======================================================================//
  // ======================================================================//
  // ======================================================================//




  // **************** AMANO ONE SPECIFIC PARCS API CALLS **************** //

  updateAmanoOneParcsAccessHolderCredentialSettings(
    lotNumber: string,
    parkerId: string,
    credentialTypeId: number,
    systemCode: string,
  ): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.baseUrl}parcs/amano/credentialtypecodes/update/${lotNumber}/${parkerId}/${credentialTypeId}/${systemCode}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Deletes an Amano One Credential System Code for a specific lotNumber and credential code.
  AmanoOne_CredentialSystemCode_Delete(lotNumber: string, credentialCode: string): Observable<boolean> {
    return this.http
      .delete<boolean>(
        `${this.baseUrl}parcs/amano/credential/systemcodes/delete/${lotNumber}/${credentialCode}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Gets a list of Amano One Credential System Codes for a specific lotNumber and credential type.
  AmanoOne_CredentialSystemCode_GetList(
    lotNumber: string,
    credentialTypeId: number,
  ): Observable<IAmanoOneCredentialSystemCodeResponse[]> {
    return this.http
      .get<
        IAmanoOneCredentialSystemCodeResponse[]
      >(`${this.baseUrl}parcs/amano/credential/systemcodes/list/${lotNumber}/${credentialTypeId}`, httpOptions)
      .pipe(this.retryTransient());
  }

  // Adds a new Amano One Credential System Code for a specific lot number and credential type.
  AmanoOne_CredentialSystemCode_AddNew(
    credentialSystemCodeData: IAmanoOneCredentialSystemCodeRequest,
  ): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.baseUrl}parcs/amano/credential/systemcode/new`, credentialSystemCodeData, httpOptions)
      .pipe(catchError(this.handleError));
  }

  AmanoOne_CredentialType_DeleteType(lotNumber: string, credentialTypeId: number): Observable<boolean> {
    return this.http
      .delete<boolean>(
        `${this.baseUrl}parcs/amano/credential/type/delete/${lotNumber}/${credentialTypeId}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Gets list of Amano One Credential Types.
  AmanoOne_CredentialType_GetList(lotNumber: string): Observable<IAmanoOneCredentialTypeResponse[]> {
    return this.http
      .get<
        IAmanoOneCredentialTypeResponse[]
      >(`${this.baseUrl}parcs/amano/credential/type/list/${lotNumber}`, httpOptions)
      .pipe(this.retryTransient());
  }

  // Adds a new Amano One Credential Type.
  AmanoOne_CredentialType_AddNew(credentialTypeData: IAmanoOneCredentialTypeRequest): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.baseUrl}parcs/amano/credential/type/new`, credentialTypeData, httpOptions)
      .pipe(catchError(this.handleError));
  }

  //todo why is this hard coded?
  ScheduleLprAsPrimaryChange(lprUpdateData: IAmanoOneScheduleLprCredentialStatusUpdateRequest): Observable<boolean> {
    return this.http
      .post<boolean>(
        'https:/dev-api-space-ar-engine-bvadaehuefagfudd.westus2-01.azurewebsites.net/api/task/notification/test',
        lprUpdateData,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Updates LPR as primary status for all Amano One Access Holders. Used the spACE-Engine API
  UpdateLprAsPrimaryChange(
    credentialSetting: boolean,
    lotNumber: string,
    notificationEmailAddress: string,
  ): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.spaceArEngineBaseUrl}parcs/amanoone/lotnumber/licenseAsCredential/${credentialSetting}/${lotNumber}/${notificationEmailAddress}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  updateExistingAmanoOneParcsAccessHolderDuringSync(
    parkerId: string,
    previousCardNumber: string,
    accessHolderId: string,
  ): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.baseUrl}parcs/amano/accessholder/sync/existing/${parkerId}/${previousCardNumber}/${accessHolderId}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  /// Adds a new Amano One Access Holder for an existing Parker profile during sync process
  postNewAmanoOneParcsAccesHolderForExistingParkerDuringSync(
    parkerId: string,
    accessGroupId: string,
  ): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.baseUrl}parcs/amano/accessholder/sync/new/${parkerId}/${accessGroupId}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // 20261023 :: Ken Moss - Updates an existing Flash account/contact from the current spACE-AR Parker data.
  // Mirrors updateExistingAmanoOneParcsAccessHolderDuringSync's contract for the Flash PARCS Sync screen's
  // "Update All Synced Parkers" action. Backed by lanai-api's ParcsController.Flash_UpdateExistingAccountFromSpaceArParker.
  updateExistingFlashAccountDuringSync(parkerId: string): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.baseUrl}parcs/flash/account/sync/existing/${parkerId}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  getParcsLotSyncDataForLotNumber(lotNumber: string): Observable<IAmanoOne_ParkerSyncComparisonForLotResponse[]> {
    return this.http
      .get<
        IAmanoOne_ParkerSyncComparisonForLotResponse[]
      >(`${this.spaceArEngineBaseUrl}parker/parcs/amanoone/comparison/${lotNumber}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // 20261023 :: Ken Moss - SANDBOX endpoint (api-space-ar-engine SandboxController.Flash_ParkerComparisonTest).
  // Requires Parcs_Flash_Parker_Tracking / Parcs_Flash_Parker_Tracking_RfidNumber to already be populated for
  // the lot (run lanai-api's POST api/sandbox/flash/daily-sync-test/{lotNumber} first). Unwraps the `results`
  // array from the sandbox endpoint's summary-count wrapper response.
  getFlashParcsLotSyncDataForLotNumber(lotNumber: string): Observable<IFlash_ParkerSyncComparisonForLotResponse[]> {
    return this.http
      .get<
        IFlash_ParkerSyncComparisonForLotApiResponse
      >(`${this.spaceArEngineBaseUrl}sandbox/flash/parker-comparison/${lotNumber}`, httpOptions)
      .pipe(
        map((res) => res.results),
        catchError(this.handleError),
      );
  }

  // Adds a new Amano One Access Holder for an existing Parker profile
  postNewAmanoOneParcsAccesHolderForExistingParker(
    lotNumber: string,
    parkerId: string,
    accessGroupId: string,
    credentialType: string,
    credentialCode: string,
    reassignCardNumber: boolean,
    cardNumber: string,
  ): Observable<boolean> {
    return this.http
      .post<boolean>(
        `${this.baseUrl}parcs/amano/accessholder/new/${lotNumber}/${parkerId}/${accessGroupId}/${credentialType}/${credentialCode}/${reassignCardNumber}/${cardNumber}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Adds a new Amano One Access Holder when creating a new Parker profile
  updateAmanoOneParcsAccessHolderDuringAddNewParker(
    parkerData: IParkerProfileDetail,
    accessHolderId: string,
  ): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.baseUrl}parcs/amano/newparkerwithexistingprofile/${accessHolderId}`,
        parkerData,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Gets two lists of access groups
  // First List is the list of Groups this access holder is a member of
  // Second list is the domain of all groups except for groups the access holder is a member of
  getAmanoOneParcsAccessGroupsLists(
    parkerId: string,
    lotNumber: string,
  ): Observable<IAmanoOne_AccessGroupsWithMembershipResponse> {
    return this.http
      .get<IAmanoOne_AccessGroupsWithMembershipResponse>(
        `${this.baseUrl}parcs/amano/accessholder/accessgroupswithmembership/${parkerId}/${lotNumber}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Removes existing Access Group from an Access Holder (Parker)
  removeAmanoOneParcsAccessGroupId(parkerId: string, lotNumber: string, accessGroupId: string): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.baseUrl}parcs/amano/accessholder/${parkerId}/${lotNumber}/${accessGroupId}/accessGroupId/remove`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Adds new Access Group to an Access Holder (Parker)
  addNewAmanoOneParcsAccessGroupId(parkerId: string, lotNumber: string, accessGroupId: string): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.baseUrl}parcs/amano/accessholder/${parkerId}/${lotNumber}/${accessGroupId}/accessGroupId/add`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Updates an Amano One Access Holder Status setting.
  // Note: The accessHolderId is an Amano One primary key value for an access holder
  updateAmanoOneParcsStatusSetting(accessHolderId: string, status: string, lotNumber: string): Observable<boolean> {
    return this.http
      .put<boolean>(
        `${this.baseUrl}parcs/amano/accessholder/status/${accessHolderId}/${status}/${lotNumber}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Updates an Amano One Access Holder Mode setting.
  // Note: The accessHolderId is an Amano One primary key value for an access holder
  updateAmanoOneParcsModeSetting(accessHolderId: string, mode: string, lotNumber: string): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.baseUrl}parcs/amano/accessholder/mode/${accessHolderId}/${mode}/${lotNumber}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // Gets an Amano One Access Holder for a given PARCS Id
  // Note: The Amano One Account Number maps to spACE AR Parker Id
  getAmanoOneParcsAccessHolderByParkerId(
    parkerId: string,
    lotNumber: string,
  ): Observable<IAmanoOneAccessHolderUpdateResponse> {
    return this.http
      .get<IAmanoOneAccessHolderUpdateResponse>(
        `${this.baseUrl}parcs/amano/accessholder/${parkerId}/${lotNumber}`,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Gets a list of Amano One Access Groups for a given Facility.
  // Note: A Facility maps to spACE AR Lots
  getAmanoOneParcsAccessGroupsByFacilityId(facilityId: string): Observable<IAmanoOneAccessGroups[]> {
    return this.http
      .get<IAmanoOneAccessGroups[]>(`${this.baseUrl}parcs/amano/accessgroups/${facilityId}`, httpOptions)
      .pipe(this.retryTransient(), catchError(this.handleError));
  }

  /// 20251001 :: Ken Moss Marked for Deletion
  // Updates the LPR Enabled Status for a given Lot Number
  // deleteAmanoConfigurationForLot(lotNumber: string): Observable<boolean> {
  //   return this.http
  //     .delete<boolean>(`${this.baseUrl}parcs/amano/${lotNumber}`, httpOptions)
  //     .pipe(catchError(this.handleError));
  // }

  // Updates the LPR Enabled Status for a given Lot Number
  updateAmanoOneParcsLprEnabledStatusForLot(lotNumber: string, lprEnabledStatus: boolean): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.baseUrl}parcs/lprEnabledStatus/amano/${lotNumber}/${lprEnabledStatus}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // Updates the Card Number Prefix for a given Lot Number
  updateAmanoOneCardNumberPrefixForLot(lotNumber: string, cardNumberPrefix: string): Observable<boolean> {
    return this.http
      .put<boolean>(`${this.baseUrl}parcs/cardNumberPrefix/amano/${lotNumber}/${cardNumberPrefix}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // Validates and returns the facility name and Device Information for a given Facility Id
  getAmanoOneFacilityNameByFacilityId(facilityId: string): Observable<IAmanoOneFacilityResponse> {
    return this.http
      .get<IAmanoOneFacilityResponse>(`${this.baseUrl}parcs/amano/facility/${facilityId}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // ============== END AMANO ONE SPECIFIC PARCS API CALLS ============== //

  // ======================================================================//
  // ======================================================================//
  // ======================================================================//

  // **************** GENERIC PARCS API CALLS **************** //

  /// Updates the PARCS Lot Configuration status to "Deleted" status for a given Lot Number
  updateParcsLotSettingsToDeletedStatusByLotNumber(lotNumber: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${this.baseUrl}parcs/lot/delete/${lotNumber}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // Gets the PARCS Lot Configuration by Lot Number
  getParcsLotConfigurationByLotNumber(lotNumber: string): Observable<IParcsGetLotConfigurationResponse> {
    return this.http
      .get<IParcsGetLotConfigurationResponse>(`${this.baseUrl}parcs/lot/config/${lotNumber}`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // Adds a new PARCS Lot Configuration
  addParcsLotFacilityConfiguration(newLotFacilityData: IParcsNewLotConfigurationRequest): Observable<number> {
    // Make sure the user didn't enter any leading or trailing spaces
    newLotFacilityData.parcsId = newLotFacilityData.parcsId.trim();
    return this.http
      .post<number>(`${this.baseUrl}parcs/lot/newconfig`, newLotFacilityData, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // Gets a list of all organizations that are PARCS vendors
  getParcsOrganizationList(): Observable<IParcsListResponse[]> {
    return this.http
      .get<IParcsListResponse[]>(`${this.baseUrl}parcs/parcsvendorlist/`, httpOptions)
      .pipe(catchError(this.handleError));
  }

  // ============== EBD GENERIC PARCS API CALLS ============== //

  private handleError(error: HttpErrorResponse) {
    console.error('handleErrorHttpRequest', error);
    if (error.error instanceof Error) {
      console.error('Error thrown: ' + JSON.stringify(error));
      const errMessage = error.error.message;
      return throwError(() => errMessage);
      // Use the following instead if using lite-server
      // return Observable.throw(err.text() || 'backend server error');
    }
    return throwError(() => error || 'server error');
  }
}
